import { useState } from 'react'
import { router } from '@inertiajs/react'
import { RefreshCw, RotateCcw, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { CostFilters as Filters } from '../types'

interface Props {
  filters: Filters
  search: string
  dateFrom: string
  dateTo: string
  organizationId: string
  digitalWorkerId: string
  model: string
  status: string
}

export default function CostFilters(props: Props) {
  const [values, setValues] = useState({
    search: props.search,
    date_from: props.dateFrom,
    date_to: props.dateTo,
    organization_id: props.organizationId,
    digital_worker_id: props.digitalWorkerId,
    model: props.model,
    status: props.status,
  })

  const setValue = (key: keyof typeof values, value: string) => {
    setValues((current) => ({ ...current, [key]: value }))
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    router.get('/cc/litellm_costs', compact(values), { preserveState: true })
  }

  const sync = () => {
    router.post('/cc/litellm_costs/sync', compact(values), { preserveScroll: true })
  }

  const clear = () => {
    router.get('/cc/litellm_costs')
  }

  return (
    <form onSubmit={submit} className="bg-card border border-border rounded-lg p-3">
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-2">
        <div className="relative md:col-span-2 xl:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={values.search}
            onChange={(event) => setValue('search', event.target.value)}
            placeholder="Buscar..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background
              focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <select
          value={values.organization_id}
          onChange={(event) => setValue('organization_id', event.target.value)}
          className="py-2 px-3 text-sm border border-border rounded-lg bg-background"
        >
          <option value="">Todas as orgs</option>
          {props.filters.organizations.map((organization) => (
            <option key={organization.id} value={organization.id}>{organization.name}</option>
          ))}
        </select>

        <select
          value={values.digital_worker_id}
          onChange={(event) => setValue('digital_worker_id', event.target.value)}
          className="py-2 px-3 text-sm border border-border rounded-lg bg-background"
        >
          <option value="">Todos os workers</option>
          {props.filters.digitalWorkers.map((worker) => (
            <option key={worker.id} value={worker.id}>{worker.name} · {worker.organizationName}</option>
          ))}
        </select>

        <select
          value={values.model}
          onChange={(event) => setValue('model', event.target.value)}
          className="py-2 px-3 text-sm border border-border rounded-lg bg-background"
        >
          <option value="">Todos os modelos</option>
          {props.filters.models.map((model) => (
            <option key={model} value={model}>{model}</option>
          ))}
        </select>

        <select
          value={values.status}
          onChange={(event) => setValue('status', event.target.value)}
          className="py-2 px-3 text-sm border border-border rounded-lg bg-background"
        >
          <option value="">Todos os status</option>
          {props.filters.statuses.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>

        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={values.date_from}
            onChange={(event) => setValue('date_from', event.target.value)}
            className="min-w-0 py-2 px-3 text-sm border border-border rounded-lg bg-background"
          />
          <input
            type="date"
            value={values.date_to}
            onChange={(event) => setValue('date_to', event.target.value)}
            className="min-w-0 py-2 px-3 text-sm border border-border rounded-lg bg-background"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={clear}>
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button type="submit" variant="outline" size="sm">
          <Search className="h-4 w-4 mr-1" />
          Filtrar
        </Button>
        <Button type="button" size="sm" onClick={sync}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Sincronizar
        </Button>
      </div>
    </form>
  )
}

function compact(values: Record<string, string>) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== ''))
}
